<template>
  <div
    id="tenant-project-documents"
    class="tenant-submission-documents"
  >
    <div class="section-header">
      <span style="margin-right: auto"> {{ $t('tenantProjects.projectDocuments') }}</span>
      <div class="tenant-project-doc__title--duration">
        <span class="lable">{{ $t('showLabel') }}:</span>
        <HKLSelect
          :value="currentBatchFilter.value"
          :options="documentHklOptions"
          dropdownClassName="tenant-project-doc__batch-dropdown"
          placeholder=""
          @change="onBatchFilterChange"
        />
      </div>
    </div>

    <div class="section-content">
      <div
        v-if="enableUpload"
        class="section-desc"
      >
        {{ $t('tenantProjects.projectDocDesc') }}
      </div>
      <div class="file-attachments">
        <div class="contents">
          <HkldDropzone
            v-if="enableUpload"
            ref="hkldDropzone1"
            :sourceId="projectDocsSrcId"
            :maxNumberOfFiles="20"
            :autoProcess="false"
          />
          <div class="column-header clearfix">
            <button
              v-if="isShowUserSubmitBtn()"
              class="btn btn--orange"
              @click="openSubmitConfirm"
            >
              {{ $t('tenantProjects.submitForReview') }}
            </button>
            <div class="titles">
              <span
                v-if="isEngineer || isRetail"
                class="flex-left"
              >
                <span @click="doSort(docTypeAttrs[0])">{{ $t('tenantProjects.design') }}</span>
                <popover
                  :text="$t('tenantProjects.designDocsTip')"
                  position="bottom-right"
                  :width="300"
                />
              </span>
              <span
                v-if="isEngineer || isRetail"
                class="flex-middle"
              >
                <span @click="doSort(docTypeAttrs[1])">{{ $t('tenantProjects.technical') }}</span>
                <popover
                  :text="$t('tenantProjects.techDocsTip')"
                  position="bottom-left"
                  :width="300"
                />
              </span>
              <span
                v-if="isEngineer"
                class="flex-right"
              >
                <span @click="doSort(docTypeAttrs[2])">{{ $t('excluded') }}</span>
              </span>
            </div>
          </div>
          <template v-for="(file, index) in projectDocsSrcIdFiles">
            <div
              v-show="isShowFile(file)"
              :key="index"
              :class="['file-panel',{'yellow-bg': isShowYellowBg(file)}, {'red-bg': file.metaData.isNotValidate}]"
            >
              <div class="panel">
                <a
                  class="download-link tender-font"
                  @click="downloadFile(file.id)"
                >
                  {{ file.filename }}
                </a>
                <div v-show="allowChangeName[file.id]">
                  <textarea
                    v-model="file.description"
                    v-autosize
                    v-checkVisibility="{refresh: true}"
                    class="doc-desc__textarea tender-font"
                    autofocus
                    @focusout="updateDescription(file)"
                  />
                </div>
                <div v-show="!allowChangeName[file.id]">
                  <span
                    class="desc-text"
                    @dblclick="() => canEditDesciption(file) && Vue.set(allowChangeName, file.id, true)"
                  >
                    {{ file.description || (!deep(file, 'metaData.submitted') ? $t('project.docs.fillDescHint') : '') }}
                  </span>
                  <i
                    v-if="canEditDesciption(file)"
                    :class="['fa fa-pencil icon', {'no-drop': isBeforeBatchNum(file)}]"
                    @click="() => Vue.set(allowChangeName, file.id, true)"
                  />
                </div>
                <p
                  v-if="deep(file, 'metaData.submitted')"
                  class="updator-text"
                >
                  {{ $t('submittedByOn', [findCreatorName(file)]) }}
                  <span class="update-date">{{ dateUtil.format(deep(file, 'metaData.submittedDate'), 'DD MMM YY') }}</span>
                  <span class="update-date">{{ dateUtil.format(deep(file, 'metaData.submittedDate'), 'HH:mm') }}</span>
                </p>
              </div>
              <div class="panel">
                <div class="choose-file-type">
                  <div
                    v-if="isEngineer || isRetail"
                    class="half flex-left"
                  >
                    <input
                      type="checkbox"
                      :checked="file.metaData.isDesign"
                      :disabled="disableTypeChange(file)"
                      @change="changeFileTypeDesign(file)"
                    >
                  </div>
                  <div
                    v-if="isEngineer || isRetail"
                    class="half flex-middle"
                  >
                    <input
                      type="checkbox"
                      :checked="file.metaData.isTechnical"
                      :disabled="disableTypeChange(file)"
                      @change="changeFileTypeTechnical(file)"
                    >
                  </div>
                  <div
                    v-if="isEngineer"
                    class="half flex-right"
                  >
                    <input
                      type="checkbox"
                      :checked="file.metaData.isNa"
                      :disabled="disableTypeChange(file)"
                      @change="changeFileTypeNa(file)"
                    >
                  </div>
                  <i
                    v-if="(!deep(file, 'metaData.submitted') && file.metaData.uploadBy === currentUser.id)"
                    class="fa fa-times fa-lg"
                    @click="openDeleteConfirm(file, projectDocsSrcIdFiles)"
                  />
                </div>
                <p
                  v-if="deep(file, 'metaData.updator.displayName')"
                  class="updator-text"
                >
                  {{ $t('tenantProjects.updatedBy', [deep(file, 'metaData.updator.displayName')]) }}
                  <span class="update-date">{{ dateUtil.format(deep(file, 'metaData.updatedDate'), 'DD MMM YY') }}</span>
                  <span class="update-date">{{ dateUtil.format(deep(file, 'metaData.updatedDate'), 'HH:mm') }}</span>
                </p>
              </div>
              <div class="batch-number">
                <span
                  v-if="!isEmpty(file.batchNum) && isShowBatchStamp(file)"
                  class="batch-icon"
                >
                  {{ $t('tenantProjects.tenantViewComments.batchNumber', [Number(file.batchNum) + 1]) }}
                </span>
                <span
                  v-if="!isWithdrawn && isEngineer && isCurrentTechReviewer && isEmpty(file.batchNum) && hasPendingSubmission && deep(file, 'metaData.submitted')"
                  class="add-batch-link"
                  @click="addToBatch(file)"
                >
                  {{ $t('tenantProjects.addToBatch') }}
                </span>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
    <ActionConfirmModal
      ref="submitConfirmModal"
      :modalId="submConfModalId"
      :confirmTitle="$t('confirmSubmit')"
    />
    <ActionConfirmModal
      ref="deleteConfirmModal"
      :modalId="deleConfModalId"
      :confirmTitle="$t('deleteConfirmMsg')"
    />
  </div>
</template>

<script>
import Vue from 'vue'
import lodash from 'lodash'
import { mapGetters, mapMutations, mapActions } from 'vuex'
import { computed, getCurrentInstance, ref } from 'vue-demi'
import { useStoreGetters } from '@/store/index'
import formFullnameMixin from '@/pages/formFullnameMixin'
import ActionConfirmModal from '@/controls/ActionConfirmModal'
import EventBus from '@/utils/eventBus'
import dateUtil from '@/utils/dateUtil'
import * as xfs from '@/utils/xfs'
import HkldDropzone from '@/controls/HkldDropzone'
import HKLSelect from '@/controls/Select/HKLSelect.vue'
import Popover from '@/controls/Popover'
import mutationTypes from '@/store/mutation-types'
import notification from '@/utils/notification'
import { ActionTypes, EmailTypes } from '@/utils/consts'
import { contactTypes } from '@/api/users/consts'
import { DesignBatchStatus, tenancyType, tenantProjectTypes } from '@/utils/locale'
import { DOCS_SORT_RULES } from '@/api/docs/consts'
import { getAttachments, updateDescription, deleteDocument, updateDocuments } from '@/api/docs/request'
import { sendEmail } from '@/api/postman/request'
import {
  doDefaultSort,
  filterExternalUserVisibleSubmissions,
  isExternalUserBatchLabelVisible,
} from '@/modules/tenantProjects/utils'
import { TenantProjectActions } from '../consts'

export default {
  name: 'TenantProjectDocuments',
  components: {
    HkldDropzone,
    ActionConfirmModal,
    Popover,
    HKLSelect,
  },
  mixins: [formFullnameMixin],
  props: {
    projectDocsSrcId: {
      type: String,
      default: '',
    },
    enableEdit: {
      type: Boolean,
      default: true,
    },
    enableUpload: {
      type: Boolean,
      default: false,
    },
  },
  setup (props) {
    const { proxy } = getCurrentInstance()
    const { currentTenantProject } = useStoreGetters('tenantProject', ['currentTenantProject'])
    const projectDocsSrcIdFiles = ref([])
    const initialDocsSrcIdFiles = ref([])

    const isRetail = computed(() => {
      return lodash.get(currentTenantProject.value, 'tenancy.type') === tenancyType.Retail
    })

    const loadFiles = async (srcId) => {
      if (!srcId) return
      let files = await getAttachments(srcId, false, DOCS_SORT_RULES.TENANT_PROJECT_DOCS)
      proxy.$parent.$emit(`controls.hkldDropzone.loadFilesLength${proxy.getFileLengthID}`, files.length)
      return doDefaultSort(files)
    }

    const initFiles = async (refreshCallback) => {
      if (!props.projectDocsSrcId) {
        return
      }
      projectDocsSrcIdFiles.value = await loadFiles(props.projectDocsSrcId)
      proxy.updateFieldByPath({
        path: ['projectDocs'],
        newValue: lodash.cloneDeep(projectDocsSrcIdFiles.value),
      })
      initialDocsSrcIdFiles.value = lodash.cloneDeep(projectDocsSrcIdFiles.value)
      if (typeof refreshCallback === 'function') await refreshCallback()
    }

    return {
      isRetail,
      loadFiles,
      initFiles,
      projectDocsSrcIdFiles,
      initialDocsSrcIdFiles,
    }
  },
  data () {
    return {
      submConfModalId: `tenantProjDocsSubmConf-${new Date().getTime()}-${Math.floor(Math.random() * 1000)}`,
      deleConfModalId: `tenantProjDocsDelConf-${new Date().getTime()}-${Math.floor(Math.random() * 1000)}`,
      allowChangeName: {},
      Vue,
      dateUtil,
      deep,
      currentBatchFilter: { label: this.$t('all'), value: -1 },
      docTypeAttrs: ['isDesign', 'isTechnical', 'isNa'],
    }
  },
  computed: {
    ...mapGetters('tenantProject', [
      'activeSubmission',
      'canTenantUpdate',
      'currentTenantProject',
      'currentTenantSubmission',
      'currentTechReviewer',
      'hasPendingSubmission',
      'isWithdrawn',
    ]),
    ...mapGetters('user', ['currentUser', 'isEngineer', 'isTenant']),
    dragOptions () {
      return {
        animation: 0,
        group: {
          name: 'tenant-project-docs',
          pull: true,
        },
        ghostClass: 'ghost',
        disabled: this.isTenant,
      }
    },
    documentOptions () {
      let isExistNewFile = this.projectDocsSrcIdFiles.some(item => {
        return !lodash.get(item, 'batchNum', '') && lodash.get(item, 'batchNum') !== 0 && lodash.get(item, 'metaData.submitted')
      })
      const batchFilterOption = [{ label: this.$t('all'), value: -1 }]
      if (isExistNewFile) {
        batchFilterOption.push({label: this.$t('action.new'), value: -2})
      }
      const submissions = lodash.get(this.currentTenantProject, 'submissions', [])
      const visibleBatchCount = this.isTenant
        ? filterExternalUserVisibleSubmissions(submissions).length
        : submissions.reduce((count, submission) => Math.max(count, Number(lodash.get(submission, 'order', -1)) + 1), 0)
      return batchFilterOption.concat(visibleBatchCount
        ? Array.from(Array(visibleBatchCount), (_, index) => ({label: this.$t('tenantProjects.tenantViewComments.batchNumber', [index + 1]), value: index}))
        : [])
    },
    displayCurrentBatchFilter () {
      return this.documentOptions.find(item => item.value === this.currentBatchFilter.value)
    },
    documentHklOptions () {
      return (this.documentOptions || []).map((opt) => ({ name: opt.label, value: opt.value }))
    },
    isCurrentTechReviewer () {
      return !!(this.currentTechReviewer &&
        this.currentUser &&
        this.currentTechReviewer.id === this.currentUser.id)
    },
    canSeedCurrentSubmissionBatch () {
      return !!(this.currentTenantSubmission &&
        this.currentUser &&
        [
          this.currentTenantSubmission.designManagedBy,
          this.currentTenantSubmission.techManagedBy,
        ].includes(this.currentUser.id))
    },
  },
  watch: {
    async projectDocsSrcId () {
      if (this.projectDocsSrcId) {
        this.setupListener('projectDocsSrcId', this.isEngineer || !this.isRetail)
      }
      await this.initFiles()
      this.validateAndSetStartReview()
    },
    async hasPendingSubmission (val) {
      this.validateAndSetStartReview()
    },
  },
  async mounted () {
    EventBus.$on('tenant_submission_created', this.assignBatchNum)
    EventBus.$on('tenant_submission_assign_batch', this.assignBatchNum)
    EventBus.$on('tenant_submission_submitted', this.openSubmitConfirm)
    EventBus.$on('tenant_docs_refresh', this.initFiles)
    this.setupListener('projectDocsSrcId', this.isEngineer || !this.isRetail)
    await this.initFiles()
    this.validateAndSetStartReview()
  },
  beforeDestroy () {
    EventBus.$off('tenant_submission_created', this.assignBatchNum)
    EventBus.$off('tenant_submission_assign_batch', this.assignBatchNum)
    EventBus.$off('tenant_submission_submitted', this.openSubmitConfirm)
    EventBus.$off('tenant_docs_refresh', this.initFiles)
    EventBus.$off(`controls.hkldDropzone.newItem.${this.projectDocsSrcId}`)
  },

  destroyed () {
    EventBus.$off('tenant_submission_created')
    EventBus.$off('tenant_submission_assign_batch')
    EventBus.$off('controls.hkldDropzone.newItem.preprojectDocsSrcId')
    EventBus.$off('tenant_docs_refresh')
  },
  methods: {
    onBatchFilterChange (value) {
      const option = (this.documentOptions || []).find((item) => item.value === value)
      this.currentBatchFilter = option || { label: this.$t('all'), value: -1 }
    },
    ...mapMutations('tenantProject', {
      setStartReview: mutationTypes.TENANT_PROJECT_SET_START_REVIEW,
      updateFieldByPath: mutationTypes.TENANT_PROJECT_UPDATE_FIELD_PATH,
      updateFieldByName: mutationTypes.TENANT_PROJECT_UPDATE_FIELD_NAME,
    }),
    ...mapActions('tenantProject', ['loadTenantProjectsDocs', 'updateCurrentStateSubmissionByPath', 'loadTenantProject', 'updateCurrentSubmissionBatchStatus']),
    onMoveCheck () {
      return !this.isTenant
    },
    isEmpty (val) {
      return val === undefined || val === null
    },
    findCreatorName (file) {
      const tenancyAccount = ((deep(this.currentTenantProject, 'tenancy.accounts') || [])
        .find(account => account.id === deep(file, 'creator.id')) || {})
        .TenancyAccount
      return tenancyAccount
        ? this.displayI18nFullName(tenancyAccount.contactFirstName, tenancyAccount.contactLastName)
        : (lodash.get(file, 'creator.displayName') || this.$t('emptyValue'))
    },
    async downloadFile (fileId) {
      await xfs.downloadFile(`/docs/${fileId}`)
    },
    async addToBatch (file) {
      if (!this.isCurrentTechReviewer) {
        return
      }
      file.batchNum = this.currentTenantSubmission.order
      file.metaData.isNewFile = true
      await updateDocuments([file], undefined, this.currentTenantProject.id)
      await this.initFiles()
      await this.setFollowupStatus([file])
    },
    init () {
      if (!this.idleTimeCheckModal) {
        this.idleTimeCheckModal = new Foundation.Reveal($('#idleTimeCheckModal'), {
          closeOnEsc: false,
          closeOnClick: false,
        })
      }
    },
    openModal () {
      this.init()
      this.idleTimeCheckModal.open()
    },
    async closeModal () {
      if (this.idleTimeCheckModal) {
        this.idleTimeCheckModal.close()
      }
    },
    dblClickEdit (id) {
      Vue.set(this.allowChangeName, id, true)
    },
    async updateDescription (file) {
      if (this.isEngineer) {
        file.metaData = {
          ...file.metaData,
          updated: true,
          updatedDate: new Date(),
        }
        if (file.metaData.submitted) {
          file.metaData.updator = {
            displayName: this.currentUser.displayName,
            id: this.currentUser.id,
            username: this.currentUser.username,
          }
        }
      }
      await updateDescription(file, { metaData: file.metaData })
      Vue.set(this.allowChangeName, file.id, false)
    },
    openDeleteConfirm (file, fileList) {
      this.$refs.deleteConfirmModal.open(this.deleteFile, [file, fileList])
    },
    async deleteFile (file, fileList) {
      const headers = {}
      if (this.action) {
        headers['X-CMS-Action'] = this.action
      }
      const deleted = await deleteDocument(file.id, headers, { markTenantProjectsStale: true })
      if (deleted) {
        const pendingDelIdx = fileList.findIndex(item => item.id === file.id)
        if (pendingDelIdx >= 0) {
          fileList.splice(pendingDelIdx, 1)
        }
      }
      this.refreshAllFiles()
    },
    async updateFiles (srcName, action) {
      await updateDocuments(this[srcName + 'Files'].map((file, index) => {
        if (!file.id) {
          return {
            ...file,
          }
        } else {
          return {
            id: file.id,
            filename: file.filename,
            source: this[srcName],
            order: index,
            batchNum: file.batchNum,
            metaData: file.metaData,
          }
        }
      }), action, this.currentTenantProject.id)
    },
    async updateAllFiles () {
      await this.updateFiles('projectDocsSrcId')
    },
    setupListener (srcName, forceTechnical) {
      if (this[srcName]) {
        EventBus.$off(`controls.hkldDropzone.newItem.${this['pre' + srcName]}`)
        EventBus.$on(`controls.hkldDropzone.newItem.${this[srcName]}`, async (newFile) => {
          await updateDocuments([{
            id: newFile.id,
            metaData: {
              uploadBy: this.currentUser.id,
              isDesign: false,
              isTechnical: !!forceTechnical,
              isNa: false,
            },
          }], undefined, this.currentTenantProject.id)
          this[srcName + 'Files'] = await this.loadFiles(this[srcName])
        })
        this['pre' + srcName] = this[srcName]
      }
    },
    async assignBatchNum () {
      if (!this.canSeedCurrentSubmissionBatch) {
        return
      }
      this.projectDocsSrcIdFiles.forEach(file => {
        if (this.isEmpty(file.batchNum) && file.metaData.submitted) {
          file.batchNum = this.currentTenantSubmission.order
        }
      })
      await this.updateAllFiles()
      await this.loadTenantProjectsDocs({projectDocsSrcId: this.projectDocsSrcId})
    },
    refreshAllFiles () {
      this.$refs.hkldDropzone1.resetMessages()
    },
    async setFollowupStatus (files) {
      let hasNewDesignFile = (files || []).some(file => lodash.get(file, 'metaData.isDesign', false) &&
        this.isCurBatchSubmittedFile(file))
      const isNoFollowupStatus = lodash.get(this.currentTenantSubmission, 'designComment.comment', '') === DesignBatchStatus.PROCEED_NO_FOLLOW_UP
      if (hasNewDesignFile && !this.currentTenantSubmission.completedDate && isNoFollowupStatus) {
        await this.autoChangeBatchStatus()
      }
    },
    openSubmitConfirm () {
      let isValidate = true
      this.projectDocsSrcIdFiles.forEach(file => {
        if (!this.checkValidatefile(file) && (file.metaData.uploadBy === this.currentUser.id || file.metaData.isNotValidate)) {
          isValidate = false
        }
      })
      this.$forceUpdate()
      if (isValidate) {
        this.$refs.submitConfirmModal.open(this.postSubmitAction)
      } else {
        notification.fireNotification(false, this.$t('tenantProjects.submission.withoutChoiceFileType'))
      }
    },
    async postSubmitAction () {
      let isValidate = true
      this.projectDocsSrcIdFiles.forEach(file => {
        if (!this.checkValidatefile(file) && file.metaData.uploadBy === this.currentUser.id) {
          isValidate = false
        }
      })
      if (isValidate) {
        const newFiles = []
        let newDesignFiles = []
        this.projectDocsSrcIdFiles.forEach(file => {
          const originFile = lodash.find(this.initialDocsSrcIdFiles, ['id', file.id])
          if (!file.metaData.submitted && file.metaData.uploadBy === this.currentUser.id) {
            deep(file, 'metaData.submitted', true)
            deep(file, 'metaData.submittedDate', +new Date())
            newFiles.push(file)
          } else if (file.metaData.isDesign && !originFile.metaData.isDesign) {
            newDesignFiles.push(file)
          }
        })
        this.validateAndSetStartReview()
        await this.updateFiles('projectDocsSrcId', TenantProjectActions.fileSubmitted)
        await this.refreshAllFiles()
        newDesignFiles = [...newDesignFiles, ...newFiles.filter(item => deep(item, 'metaData.isDesign'))]
        await this.setFollowupStatus(newDesignFiles)
        if (newDesignFiles.length && this.currentTenantProject.type === tenantProjectTypes.Fitout) {
          sendEmail(EmailTypes.tenantProjects.submitDesignDocs)
        }
        await this.loadTenantProjectsDocs({projectDocsSrcId: this.projectDocsSrcId})
      } else {
        notification.fireNotification(false, this.$t('tenantProjects.submission.withoutChoiceFileType'))
      }
      await this.initFiles()
      this.$forceUpdate()
    },
    validateAndSetStartReview () {
      if (this.projectDocsSrcIdFiles === undefined) return
      if (!this.hasPendingSubmission && lodash.isEmpty(this.projectDocsSrcIdFiles.filter(file => this.isEmpty(file.batchNum) && file.metaData.submitted))) {
        this.setStartReview({value: false})
      } else {
        this.setStartReview({value: true})
      }
    },
    async changeFileTypeTechnical (file) {
      file.metaData.isTechnical = !file.metaData.isTechnical
      if (this.isEngineer) {
        file.metaData.isNa = !file.metaData.isDesign && !file.metaData.isTechnical
      }
      await this.changeFileType(file)
    },
    async changeFileTypeDesign (file) {
      file.metaData.isDesign = !file.metaData.isDesign
      if (this.isEngineer) {
        file.metaData.isNa = !file.metaData.isDesign && !file.metaData.isTechnical
      }
      await this.changeFileType(file)
    },
    async changeFileTypeNa (file) {
      file.metaData.isTechnical = file.metaData.isNa
      file.metaData.isNa = !file.metaData.isNa
      file.metaData.isDesign = false
      await this.changeFileType(file)
    },
    async changeFileType (file) {
      this.checkValidatefile(file)
      if (!file.metaData.submitted) {
        delete file.metaData.isNotValidate
      }
      this.doUpdateFileType(file)
      this.$forceUpdate()
    },
    isCurBatchSubmittedFile (file) {
      return file.metaData.submitted && !['', null].includes(file.batchNum) && Number(file.batchNum) === this.currentTenantSubmission.order
    },
    async autoChangeBatchStatus () {
      this.updateCurrentStateSubmissionByPath({
        path: ['designComment', 'comment'],
        newValue: DesignBatchStatus.PROCEED_CP_FOLLOW_UP,
      })
      this.updateCurrentStateSubmissionByPath({path: ['designComment', 'submittedDate'], newValue: new Date()})

      const designComment = JSON.parse(JSON.stringify(deep(this.currentTenantSubmission, 'designComment') || {}))
      this.updateCurrentStateSubmissionByPath({path: ['submittedDesignComment'],
        newValue: lodash.cloneDeep(designComment)})

      const auditLogAction = ActionTypes.tenantProjects.designComment.batchStatusChanged
      await this.updateCurrentSubmissionBatchStatus(auditLogAction)
      await sendEmail(EmailTypes.tenantProjects.batchStatusChanged, { isAutoChangeStatus: true })
      await this.loadTenantProject(this.currentTenantProject.id)
    },
    isFileTypeChanged (file) {
      const originFile = lodash.find(this.initialDocsSrcIdFiles, ['id', file.id])
      return !!(originFile && file.metaData.submitted &&
        this.docTypeAttrs.some(type => file.metaData[type] !== originFile.metaData[type]))
    },
    doUpdateFileType (file) {
      if (!this.isFileTypeChanged(file)) {
        const oldFile = lodash.find(this.initialDocsSrcIdFiles, ['id', file.id])
        if (oldFile && file.metaData.submitted) { file.metaData = lodash.cloneDeep(oldFile.metaData) }
        return
      }
      const metaData = {
        ...file.metaData,
        updatedDate: new Date(),
      }
      if (this.isEngineer && file.metaData.submitted) {
        metaData.isUserUpdated = true
      }

      if (file.metaData.submitted) {
        metaData.updator = {
          displayName: this.currentUser.displayName,
          id: this.currentUser.id,
          username: this.currentUser.username,
        }
      }
      file.metaData = metaData
    },
    isShowYellowBg (file) {
      if (this.isBeforeBatchNum(file)) {
        return false
      }
      return (this.hasPendingSubmission && file.metaData.submitted && file.metaData.isNewFile) || file.metaData.isUserUpdated
    },
    checkValidatefile (file) {
      let validate = this.currentTenantProject.type === tenantProjectTypes.Reinstatement ||
        !this.isShowFile(file) || file.metaData.isDesign || file.metaData.isTechnical || file.metaData.isNa
      if (validate) {
        delete file.metaData.isNotValidate
      } else {
        file.metaData.isNotValidate = true

        if (!this.isEngineer && !this.isRetail) {
          file.metaData.isTechnical = true
          delete file.metaData.isNotValidate
          validate = true
        }
      }
      return validate
    },
    isShowFile (file) {
      return this.filterFiles(file) && (deep(file, 'metaData.submitted') || deep(file, 'metaData.uploadBy') === this.currentUser.id)
    },
    filterFiles (file) {
      const batchFilterNum = lodash.get(this.currentBatchFilter, 'value', -1)
      let ret = true
      switch (batchFilterNum) {
        case -1:
          ret = true
          break
        case -2:
          ret = this.isEmpty(file.batchNum) && deep(file, 'metaData.submitted')
          break
        default:
          ret = !this.isEmpty(file.batchNum) && batchFilterNum === Number(file.batchNum)
          break
      }
      return ret
    },
    getUserEmail (user) {
      if (!user) { return {} }
      const contact = (user.contacts || []).filter(contact => contact.type === contactTypes.EMAIL)[0]
      const email = contact ? contact.value : user.username
      return email
    },
    disableTypeChange (file) {
      return (!this.enableEdit && (file.createdBy !== this.currentUser.id || deep(file, 'metaData.submitted'))) ||
        this.isBeforeBatchNum(file)
    },
    isShowBatchStamp (file) {
      if (!this.isTenant) {
        return true
      }
      return isExternalUserBatchLabelVisible(
        lodash.get(this.currentTenantProject, 'submissions', []),
        file.batchNum,
      )
    },
    isBeforeBatchNum (file) {
      if (!this.currentTenantSubmission) {
        return false
      }
      const currentBatchNum = this.currentTenantSubmission.order
      if (currentBatchNum === -1 || file.batchNum === null) {
        return false
      }
      return this.hasPendingSubmission
        ? Number(file.batchNum) < currentBatchNum
        : Number(file.batchNum) <= currentBatchNum
    },
    canEditDesciption (file) {
      return (this.enableEdit || file.metaData.uploadBy === this.currentUser.id) &&
        !this.isBeforeBatchNum(file) && !deep(file, 'metaData.submitted')
    },
    doSort (attr) {
      let attrs = this.docTypeAttrs.filter(item => item !== attr)
      let sortFuncs = [attr, ...attrs, 'submittedDate'].map(item => (obj) => (obj.metaData[item] || ''))
      let orders = ['desc', 'desc', 'desc', 'desc']
      this.projectDocsSrcIdFiles = lodash.orderBy(this.projectDocsSrcIdFiles, sortFuncs, orders)
    },
    isShowUserSubmitBtn () {
      let newFile = false
      this.projectDocsSrcIdFiles && this.projectDocsSrcIdFiles.map(file => {
        if (!file.metaData.submitted && file.metaData.uploadBy === this.currentUser.id) {
          newFile = true
        }
      })
      if (newFile && this.isTenant) {
        newFile = this.canTenantUpdate
      }

      let isFileTypeChange = false
      this.projectDocsSrcIdFiles && this.projectDocsSrcIdFiles.map(file => {
        if (this.isFileTypeChanged(file)) {
          isFileTypeChange = true
        }
      })
      return newFile || isFileTypeChange
    },
  },
}
</script>

<style lang="scss" scoped src="styles/tenants/submission-docs.scss"></style>
<style lang="scss">
.tenant-submission-documents {
  &#tenant-project-documents .doc-desc__textarea {
    width: calc(100% - 16px);
    margin-right: 16px;
  }

  .no-drop {
    cursor: no-drop;
  }
  .choose-file-type {
    height: 20px;
  }
  .batch-number {
    text-align: center;
    position: absolute;
    top: 0px;
    right: 0px;
  }
  .column-header .titles{
    margin-right: 15px !important;
    .flex-right {
      span {
        cursor: pointer;
      }
    }
    .flex-left {
      span {
        cursor: pointer;
      }
    }
  }
}
.tenant-project-doc__title--duration {
  width: 160px;
  margin: 0 !important;
  .multiselect,
  .hkl-select {
    width: 100% !important;
  }
  .lable {
    line-height: 48px;
  }
  & .multiselect,
  & .hkl-select {
    margin-top: 5px;
  }

  & .hkl-select {
    height: 24px;
    min-height: 24px;
    border: none !important;
    background-color: transparent;

    .ant-select-selection {
      min-height: 24px;
      height: 24px;
      background-color: transparent;
    }

    .ant-select-selection__rendered {
      min-height: 24px;
      height: 24px;
      line-height: 24px;
      margin-left: 10px;
      margin-right: 40px;
    }

    .ant-select-arrow {
      right: 0;
      width: 40px;
      height: 38px;
      margin-top: -12px;
      line-height: 36px;
      text-align: center;
      color: #0C3A58;

      .ant-select-arrow-icon {
        display: none;
      }

      &::before {
        content: "\F107";
        display: inline-block;
        font: normal normal normal 25px/36px FontAwesome;
        text-rendering: auto;
        transition: transform 0.2s ease;
        -webkit-font-smoothing: antialiased;
      }
    }

    &.ant-select-open .ant-select-arrow::before {
      transform: rotateZ(180deg);
    }
  }
}

.ant-select-dropdown.hkl-select-dropdown.tenant-project-doc__batch-dropdown {
  min-width: 110px !important;
}
</style>
